import { ChevronDown, Search, X } from "lucide-react";

import { Input } from "@/app/admin/_common/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import CuoButton from "@/app/admin/_common/CuoButton";

import {
  CHANNEL_FILTER_OPTIONS,
  SOURCE_FILTER_OPTIONS,
  STATUS_FILTER_OPTIONS,
} from "./messageStatus";

function FilterDropdown({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  const activeLabel = options.find((o) => o.value === value)?.label ?? label;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-[30px] items-center gap-1.5 rounded-md border border-input bg-white px-3 text-[12.5px] text-foreground hover:bg-muted"
        >
          <span className="hidden text-muted-foreground sm:inline">{label}:</span>
          {activeLabel}
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuRadioGroup value={value} onValueChange={onChange}>
          {options.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value}>
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function CommunicationsToolbar({
  search,
  onSearchChange,
  channelFilter,
  onChannelFilterChange,
  statusFilter,
  onStatusFilterChange,
  sourceFilter,
  onSourceFilterChange,
  hasFilters,
  onClearFilters,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  channelFilter: string;
  onChannelFilterChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  sourceFilter: string;
  onSourceFilterChange: (value: string) => void;
  hasFilters: boolean;
  onClearFilters: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative min-w-0 max-w-[420px] flex-1">
        <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
          <Search className="size-3.5 text-muted-foreground" />
        </div>
        <Input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Email, phone or subject…"
          className="h-[30px] pl-9 text-[12.5px]"
          aria-label="Search communications"
        />
      </div>

      <FilterDropdown
        label="Channel"
        value={channelFilter}
        options={CHANNEL_FILTER_OPTIONS}
        onChange={onChannelFilterChange}
      />
      <FilterDropdown
        label="Status"
        value={statusFilter}
        options={STATUS_FILTER_OPTIONS}
        onChange={onStatusFilterChange}
      />
      <FilterDropdown
        label="Source"
        value={sourceFilter}
        options={SOURCE_FILTER_OPTIONS}
        onChange={onSourceFilterChange}
      />

      {hasFilters && (
        <CuoButton
          variant="ghost"
          label="Clear"
          icon={<X className="size-3.5 shrink-0" size={14} />}
          onClick={onClearFilters}
        />
      )}
    </div>
  );
}
